import {
  PrimaryButton,
  PrimaryOutlineButton,
} from '@/components/button/button-component';
import { Col, Row } from '@/components/grid/grid-component';
import Modal from '@/components/modal/modal-component';
import { FileDropZone } from '@/components/react-hook-form-inputs/file-dropzone/file-dropzone-component';
import { RawInput } from '@/components/react-hook-form-inputs/input/input-component';
import PropTypes from 'prop-types';
import { useState } from 'react';
import {
  Controller,
  useFieldArray,
  useForm,
  useFormContext,
} from 'react-hook-form';
import { FiX } from 'react-icons/fi';
import { MdAdd } from 'react-icons/md';
import Styled from './product-document-upload-style';

/**
 * Document Uploader Component
 * @param {documents} docs docs to display
 * @returns {JSX.Element}
 */
const DocumentUploader = ({ name, handleMultiSelectChange }) => {
  const [uploadModalOpen, setUploadModalOpen] = useState(false);

  const { control, register, handleSubmit, formState, reset } = useForm();
  const { control: fcControl } = useFormContext();

  const { fields, remove, insert } = useFieldArray({
    control: fcControl,
    name, // unique name for your Field Array
  });

  const addFile = (e) => {
    insert(fields.length, e);
    handleMultiSelectChange?.([...fields, { name: '', size: 0 }]);
    reset();
    setUploadModalOpen(false);
  };

  const removeFile = (index, e) => {
    e.preventDefault();
    remove(index);
    const updatedFields = fields.filter((_, i) => i !== index);
    handleMultiSelectChange?.(updatedFields);
  };

  const handleSelectFiles = () => {
    setUploadModalOpen(true);
  };

  return (
    <>
      {fields.length > 0 && (
        <Styled.FileListWrapper>
          {fields.map((file, index) => (
            <Styled.FileItem key={`file_item_${index + 1}`}>
              <Styled.FileIconContainer>
                <Styled.StyledFileIcon />
              </Styled.FileIconContainer>
              <Styled.FileName>
                <Styled.FileLabel>{file.name}</Styled.FileLabel>
                <Styled.FileSize>{file.size} kb </Styled.FileSize>
              </Styled.FileName>
              <Styled.StyledCloseIcon onClick={(e) => removeFile(index, e)} />
            </Styled.FileItem>
          ))}
        </Styled.FileListWrapper>
      )}
      <Modal
        width="50%"
        alignItems="center"
        open={uploadModalOpen}
        close={() => setUploadModalOpen(false)}
        bodyStyle={{ padding: '24px' }}
      >
        <Row $margin="0px">
          <Col xs={12}>
            <Controller
              control={control}
              name="file"
              defaultValue={null}
              rules={{
                required: 'File is required',
              }}
              render={({ field: { value, onChange } }) => (
                <FileDropZone
                  headerText="Upload File"
                  raw
                  renderPreview={false}
                  cy="input-upload-file"
                  onChange={onChange}
                  cyDropzone="dz-upload-file"
                  image={value}
                />
              )}
            />
          </Col>
          <Col xs={12}>
            <RawInput
              name="name"
              label="File Description/Name"
              register={register}
              formState={formState}
              registerOptions={{
                required: 'File name is required',
              }}
            />
          </Col>
          <Styled.StyledButtonCol xs={12}>
            <PrimaryOutlineButton
              buttonWidth="108px"
              type="Button"
              loading={false}
              title="Cancel"
              onClick={() => setUploadModalOpen(false)}
              icon={<FiX />}
              margin="0 16px 0 0"
            />
            <PrimaryButton
              buttonWidth="108px"
              type="submit"
              loading={false}
              title="Save"
              onClick={handleSubmit((submit) => {
                addFile(submit);
              })}
            />
          </Styled.StyledButtonCol>
        </Row>
      </Modal>

      <Styled.DocumentUploadAction onClick={handleSelectFiles}>
        <MdAdd /> <Styled.ActionText>Add Document</Styled.ActionText>
      </Styled.DocumentUploadAction>
    </>
  );
};

DocumentUploader.defaultProps = {
  handleMultiSelectChange: undefined,
};

DocumentUploader.propTypes = {
  name: PropTypes.string.isRequired,
  handleMultiSelectChange: PropTypes.func,
};

export default DocumentUploader;
